import { createContext, useContext, ReactNode, useMemo } from "react";

/**
 * Paywalls have been removed — every feature is free for all users and the
 * app is funded by advertising. This provider stays in place so existing
 * feature checks keep working, and always reports full access.
 */
interface SubscriptionContextType {
  isSubscribed: boolean;
  isLoading: boolean;
  productId: string | null;
  subscriptionEnd: string | null;
  checkSubscription: () => Promise<void>;
  subscribe: () => Promise<void>;
  restoreSubscription: () => Promise<void>;
}

const noop = async () => {};

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export const SubscriptionProvider = ({ children }: { children: ReactNode }) => {
  const value = useMemo<SubscriptionContextType>(
    () => ({
      isSubscribed: true,
      isLoading: false,
      productId: null,
      subscriptionEnd: null,
      checkSubscription: noop,
      subscribe: noop,
      restoreSubscription: noop,
    }),
    []
  );

  return <SubscriptionContext.Provider value={value}>{children}</SubscriptionContext.Provider>;
};

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error("useSubscription must be used within a SubscriptionProvider");
  }
  return context;
};
